import { createSlice, createListenerMiddleware } from '@reduxjs/toolkit';
import {
  ComercialBankAccount412Error,
  BankAccountMaxAttempts422Error
} from 'errors/BankAccountValidationError';
import { resetStateToInitial } from 'state/reset-state/reset-state-actions';

import type { PayloadAction } from '@reduxjs/toolkit';
import type { NormalizedError } from 'errors/types';
import type { PremisesResponse } from 'api/models/TAddress';

export type DirectDebitState = {
  isLoading: {
    directDebit: boolean;
    premises: boolean;
  };
  errors: {
    directDebit: null | NormalizedError[];
    premises: null | NormalizedError[];
  };
  data: {
    directDebit: Record<string, unknown>;
    premises: PremisesResponse;
  };
  details: {
    firstName: string | null;
    lastName: string | null;
    accountHolderName: string | null;
    birthDate: string | null;
    sortCode: string | null;
    accountNumber: string | null;
    isSupplyAddress: boolean | null;
    address: string | null;
    postcode: string | null;
    paymentDate: string | null;
    companyName: string | null;
    companyRegistrationNumber: string | null;
    isCommercial: boolean;
  };
  attemptsCount: number;
};

export const initialState: DirectDebitState = {
  isLoading: {
    directDebit: false,
    premises: false
  },
  errors: {
    directDebit: null,
    premises: null
  },
  data: {
    directDebit: {},
    premises: {}
  },
  details: {
    firstName: null,
    lastName: null,
    accountHolderName: null,
    birthDate: null,
    sortCode: null,
    accountNumber: null,
    isSupplyAddress: null,
    address: null,
    postcode: null,
    paymentDate: null,
    companyName: null,
    companyRegistrationNumber: null,
    isCommercial: false
  },
  attemptsCount: 0
};

export const slice = createSlice({
  name: 'directDebit',
  initialState,
  reducers: {
    setInitialState: (state) => {
      Object.entries(initialState).forEach(([key, value]) => {
        state[key] = value;
      });
    },
    setIsLoadingPremisses: (state) => {
      state.isLoading.premises = true;
    },
    setIsLoadingDirectDebit: (state, { payload }) => {
      state.isLoading.directDebit = payload ?? true;
    },
    setSuccessDirectDebit: (state, { payload }) => {
      state.isLoading.directDebit = false;
      state.data.directDebit = payload;
      state.errors.directDebit = initialState.errors.directDebit;
    },
    setSuccessPremises: (state, { payload }) => {
      state.isLoading.premises = false;
      state.data.premises = payload;
      state.errors.premises = initialState.errors.premises;
    },
    setDirectDebitError: (state, { payload }) => {
      state.isLoading.directDebit = false;

      state.errors.directDebit = payload;
    },
    setPremisesError: (state, { payload }) => {
      state.isLoading.premises = false;
      state.errors.premises = payload;
    },
    setDirectDebitField: (state, { payload: { name, value } }) => {
      state.details[name] = value;
    },
    resetPremisesErrors: (state) => {
      state.errors.premises = initialState.errors.premises;
    },
    resetAddressSelector: (state) => {
      state.errors.premises = initialState.errors.premises;
      state.data.premises = initialState.data.premises;
      state.details.postcode = initialState.details.postcode;
    },
    resetSelectedAddress: (state) => {
      state.details.address = initialState.details.address;
    },
    resetDetails: (state) => {
      state.details = initialState.details;
    },
    incrementAttempts: (
      state,
      { payload }: PayloadAction<number | undefined>
    ) => {
      state.attemptsCount = payload ? payload : state.attemptsCount + 1;
    },
    resetDirectDebitErrors: (state) => {
      state.errors.directDebit = initialState.errors.directDebit;
    }
  }
});

export const {
  setInitialState,
  setIsLoadingDirectDebit,
  setIsLoadingPremisses,
  setSuccessPremises,
  setSuccessDirectDebit,
  setPremisesError,
  setDirectDebitError,
  setDirectDebitField,
  resetPremisesErrors,
  resetAddressSelector,
  incrementAttempts,
  resetDetails,
  resetDirectDebitErrors,
  resetSelectedAddress
} = slice.actions;

export const selectSlice = (state) => state[slice.name];
export const selectIsLoading = (state) => selectSlice(state).isLoading;
export const selectIsLoadingDirectDebit = (state) =>
  selectIsLoading(state).directDebit;
export const selectIsLoadingPremises = (state) =>
  selectIsLoading(state).premises;
export const selectErrors = (state) => selectSlice(state).errors;
export const selectDirectDebitErrors = (state) =>
  selectErrors(state)?.directDebit?.filter(
    (error) => error.name !== ComercialBankAccount412Error.name // not displayed to the user
  ) ?? initialState.errors.directDebit;
export const selectHasMaxAttemptsError = (state) =>
  !!selectErrors(state)?.directDebit?.some(
    (error) => error?.name === BankAccountMaxAttempts422Error.name
  );
export const selectPremissesErrors = (state) => selectErrors(state).premises;
export const selectData = (state) => selectSlice(state).data;
export const selectDirectDebitData = (state) => selectData(state).directDebit;
export const selectPremisesData = (state) => selectData(state).premises;
export const selectDetails = (state) => selectSlice(state).details;
export const selectAttempts = (state) => selectSlice(state).attemptsCount;

// middleware
export const preventMaxAttemptErrorResetMiddleware = createListenerMiddleware();

/**
 * Prevent BankAccountMaxAttempts422Error from being removed from state by restoring it.
 * Also restores attempts counter when restoring the BankAccountMaxAttempts422Error
 */
preventMaxAttemptErrorResetMiddleware.startListening({
  predicate: (action, currentState, previousState) => {
    // resetStateToInitial should still work
    const isResetStateToInitial = action.type === resetStateToInitial.type;
    const currentHasMaxAttempsError = selectHasMaxAttemptsError(currentState);
    const previousHasMaxAttempsError = selectHasMaxAttemptsError(previousState);

    return (
      !isResetStateToInitial &&
      previousHasMaxAttempsError &&
      !currentHasMaxAttempsError
    );
  },
  effect: (_action, listenerApi) => {
    const previousState = listenerApi.getOriginalState();
    const previousDDErrors = selectDirectDebitErrors(previousState);
    const previousAttempts = selectAttempts(previousState);

    listenerApi.dispatch(setDirectDebitError(previousDDErrors));
    listenerApi.dispatch(incrementAttempts(previousAttempts));
  }
});

export default slice.reducer;
